/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LightbulbProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LightbulbProps {
    /**
     * A string representing the CSS class to be applied to the LightbulbIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#27272A'
     */
    color1?: string;
    /**
     * A number or string representing the height of the LightbulbIcon element.
     *
     * @default 20
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Lightbulb'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LightbulbIcon element.
     *
     * @default 20
     */
    width?: number | string;
}

/**
 * The `Lightbulb` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LightbulbProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LightbulbIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LightbulbIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LightbulbIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LightbulbComponent = <Lightbulb className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Lightbulb = ({
    className = '',
    color1 = '#27272A',
    height = 20,
    ref,
    testId = 'Lightbulb',
    width = 20
}: LightbulbProps) => (
    <svg
        ref={ref}
        aria-label="Info"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 20 20"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M11.67 17.5a.83.83 0 0 1 0 1.67H8.33a.83.83 0 1 1 0-1.67zm.83-3.33a.83.83 0 0 1 0 1.66h-5a.83.83 0 1 1 0-1.66zm1.67-7.5A4.17 4.17 0 0 0 10.2 2.5H10a4.17 4.17 0 0 0-4.18 4.17l.01.25a3 3 0 0 0 1 2.07 5 5 0 0 1 1.48 2.51.83.83 0 0 1-1.64.33 3.3 3.3 0 0 0-1.02-1.66 4.7 4.7 0 0 1-1.48-3.12l-.01-.38A5.83 5.83 0 0 1 10 .83l.29.01a5.83 5.83 0 0 1 5.54 5.83c0 1.27-.48 2.58-1.49 3.5-.61.61-.9 1.06-1.02 1.66a.83.83 0 0 1-1.64-.33A4.7 4.7 0 0 1 13.16 9l.03-.04c.64-.57.98-1.42.98-2.3" fill={color1} />
    </svg>
);

Lightbulb.displayName = 'Lightbulb';